import React from 'react';

/**
 * Terms of Service — mirrored from sparvi.io/terms-of-service so it can be
 * shown inline during signup without redirecting. Keep aligned with
 * sparvi.io/pages/terms-of-service.tsx when legal text changes.
 *
 * Last updated: October 1, 2025
 */
const TermsContent = () => (
  <>
    <p><strong>Last Updated: October 1, 2025</strong></p>

    <h2>1. Introduction</h2>
    <p>
      Welcome to Sparvi. Please read these Terms of Service (the &quot;Terms&quot;) carefully as
      they govern your use of our website located at sparvi.io (the &quot;Site&quot;) and our
      collaborative data observability services, including any associated mobile and
      desktop applications (collectively, the &quot;Service&quot;) operated by Sparvi LLC
      (&quot;Sparvi&quot;, &quot;we,&quot; &quot;us,&quot; or &quot;our&quot;).
    </p>
    <p>
      By accessing or using the Service, you agree to be bound by these Terms. If you
      disagree with any part of the Terms, you may not access the Service.
    </p>

    <h2>2. Definitions</h2>
    <ul>
      <li><strong>&quot;Account&quot;</strong> means a unique account created for you to access our Service.</li>
      <li><strong>&quot;Content&quot;</strong> means any information, data, text, software, graphics, messages, or other materials that are uploaded, generated, provided or otherwise made available through the Service.</li>
      <li><strong>&quot;User&quot;</strong> means any individual who accesses or uses the Service.</li>
      <li><strong>&quot;Subscriber&quot;</strong> means a User that has registered for a paid account.</li>
    </ul>

    <h2>3. Account Registration and Eligibility</h2>

    <h3>3.1 Account Creation</h3>
    <p>
      To use certain features of the Service, you must register for an account. You agree
      to provide accurate, current, and complete information during the registration
      process and to update such information to keep it accurate, current, and complete.
    </p>

    <h3>3.2 Account Responsibilities</h3>
    <p>You are responsible for:</p>
    <ul>
      <li>Maintaining the confidentiality of your account credentials</li>
      <li>All activities that occur under your account</li>
      <li>Notifying us immediately of any unauthorized use of your account</li>
    </ul>

    <h3>3.3 Eligibility</h3>
    <p>
      You must be at least 16 years old to use the Service. By using the Service, you
      represent and warrant that you meet this requirement.
    </p>

    <h2>4. Subscription and Payment Terms</h2>

    <h3>4.1 Free and Paid Services</h3>
    <p>
      We offer both free and paid versions of our Service. Features included in each plan
      are described on our pricing page. New subscribers may receive a free trial of paid
      features for a defined period; trial accounts automatically revert to the free tier
      at the end of the trial unless converted to a paid subscription.
    </p>

    <h3>4.2 Payment and Billing</h3>
    <p>If you subscribe to a paid version of the Service:</p>
    <ul>
      <li>You agree to pay all fees applicable to your subscription plan</li>
      <li>Fees are payable in advance and non-refundable except as otherwise provided in these Terms</li>
      <li>Subscription fees will automatically renew until canceled</li>
      <li>You authorize us to charge your payment method for all fees due</li>
    </ul>

    <h3>4.3 Changes to Fees</h3>
    <p>
      We may change our subscription fees at any time. If we reduce subscription fees, the
      reduction will become effective immediately. If we increase subscription fees, we
      will provide notice at least 30 days before the increase takes effect.
    </p>

    <h2>5. Acceptable Use</h2>

    <h3>5.1 Compliance with Laws</h3>
    <p>
      You agree to use the Service in compliance with all applicable laws, regulations,
      and third party rights.
    </p>

    <h3>5.2 Prohibited Activities</h3>
    <p>You agree not to:</p>
    <ul>
      <li>Use the Service in any manner that could interfere with, disable, disrupt, overburden, or otherwise impair the Service</li>
      <li>Attempt to gain unauthorized access to any portion of the Service or any other accounts, systems, or networks</li>
      <li>Use the Service for any illegal purpose or to violate any laws or regulations</li>
      <li>Use any robot, spider, or other automatic device to access the Service</li>
      <li>Transmit any viruses, malware, or other harmful computer code</li>
      <li>Use the Service to store or transmit content that infringes on intellectual property rights</li>
      <li>Reverse engineer any aspect of the Service or do anything that might discover source code</li>
      <li>Attempt to bypass any measures we may use to prevent or restrict access to the Service</li>
    </ul>

    <h2>6. Content</h2>

    <h3>6.1 Your Content</h3>
    <p>
      You retain all rights to any Content you submit, post, or display on or through the
      Service. By submitting, posting, or displaying Content on or through the Service,
      you grant us a worldwide, non-exclusive, royalty-free license to use, copy, modify,
      display, and distribute such Content to provide and improve the Service.
    </p>

    <h3>6.2 Content Responsibility</h3>
    <p>
      You are solely responsible for your Content and the consequences of submitting and
      publishing your Content. You represent and warrant that:
    </p>
    <ul>
      <li>You have all necessary rights to submit your Content</li>
      <li>Your Content does not violate any third party&apos;s rights</li>
      <li>Your Content complies with these Terms and all applicable laws and regulations</li>
    </ul>

    <h3>6.3 Our Rights</h3>
    <p>
      We reserve the right to remove any Content that violates these Terms or that we find
      objectionable for any reason, without prior notice.
    </p>

    <h2>7. Intellectual Property Rights</h2>

    <h3>7.1 Sparvi Intellectual Property</h3>
    <p>
      The Service and its content, features, and functionality are owned by Sparvi and are
      protected by copyright, trademark, patent, trade secret, and other intellectual
      property or proprietary rights laws. These Terms do not grant you any right, title,
      or interest in the Service, our trademarks, logos, or other intellectual property.
    </p>

    <h3>7.2 License to Use the Service</h3>
    <p>
      Subject to these Terms, we grant you a limited, non-exclusive, non-transferable, and
      revocable license to use the Service for your personal or internal business
      purposes.
    </p>

    <h2>8. Third-Party Services</h2>
    <p>
      The Service may contain links to third-party websites or services. We are not
      responsible for the content or practices of any third-party websites or services and
      do not endorse them. Your use of third-party websites or services is subject to
      their terms and conditions.
    </p>

    <h2>9. Termination</h2>

    <h3>9.1 Termination by You</h3>
    <p>You may terminate your account and discontinue use of the Service at any time.</p>

    <h3>9.2 Termination by Us</h3>
    <p>
      We may terminate or suspend your account and access to the Service immediately,
      without prior notice or liability, for any reason, including if you breach these
      Terms.
    </p>

    <h3>9.3 Effect of Termination</h3>
    <p>
      Upon termination, your right to use the Service will immediately cease. All
      provisions of these Terms which by their nature should survive termination shall
      survive, including without limitation, ownership provisions, warranty disclaimers,
      indemnity, and limitations of liability.
    </p>

    <h2>10. Disclaimers</h2>
    <p>
      THE SERVICE IS PROVIDED &quot;AS IS&quot; AND &quot;AS AVAILABLE&quot; WITHOUT WARRANTIES OF ANY KIND,
      EITHER EXPRESS OR IMPLIED, INCLUDING, BUT NOT LIMITED TO, IMPLIED WARRANTIES OF
      MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, AND NON-INFRINGEMENT. WE
      DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR SECURE.
    </p>

    <h2>11. Limitation of Liability</h2>
    <p>
      IN NO EVENT SHALL SPARVI, ITS OFFICERS, DIRECTORS, EMPLOYEES, OR AGENTS, BE LIABLE
      FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL OR PUNITIVE DAMAGES, INCLUDING
      WITHOUT LIMITATION, LOSS OF PROFITS, DATA, USE, GOODWILL, OR OTHER INTANGIBLE
      LOSSES, RESULTING FROM YOUR ACCESS TO OR USE OF OR INABILITY TO ACCESS OR USE THE
      SERVICE.
    </p>

    <h2>12. Indemnification</h2>
    <p>
      You agree to defend, indemnify, and hold harmless Sparvi and its officers,
      directors, employees, and agents, from and against any claims, liabilities, damages,
      losses, and expenses, including, without limitation, reasonable legal and accounting
      fees, arising out of or in any way connected with your access to or use of the
      Service or your violation of these Terms.
    </p>

    <h2>13. Changes to the Terms</h2>
    <p>
      We reserve the right to modify these Terms at any time. If we make material changes
      to these Terms, we will provide notice through the Service or by other means. Your
      continued use of the Service after the changes take effect constitutes your
      acceptance of the revised Terms.
    </p>

    <h2>14. Governing Law</h2>
    <p>
      These Terms shall be governed by and construed in accordance with the laws of the
      State of Delaware, without regard to its conflict of law provisions.
    </p>

    <h2>15. Dispute Resolution</h2>
    <p>
      Any dispute arising from or relating to the subject matter of these Terms shall be
      finally settled by arbitration in Delaware, using the English language in accordance
      with the rules of the American Arbitration Association.
    </p>

    <h2>16. Severability</h2>
    <p>
      If any provision of these Terms is held to be unenforceable or invalid, such
      provision will be changed and interpreted to accomplish the objectives of such
      provision to the greatest extent possible under applicable law and the remaining
      provisions will continue in full force and effect.
    </p>

    <h2>17. Entire Agreement</h2>
    <p>
      These Terms constitute the entire agreement between you and Sparvi regarding our
      Service and supersede all prior agreements and understandings, whether written or
      oral.
    </p>

    <h2>18. Contact Information</h2>
    <p>If you have any questions about these Terms, please contact us:</p>
    <p>
      <strong>Email</strong>:{' '}
      <a href="mailto:contact@sparvi.io">contact@sparvi.io</a>
    </p>
  </>
);

export default TermsContent;
